import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { Star } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import type { Deal } from "@/types/deals"

interface DealCardProps {
  deal: Deal
  index?: number
  className?: string
}

export function DealCard({ deal, index = 0, className = "" }: Omit<DealCardProps, 'onViewDeal'>) {
  const navigate = useNavigate()

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(price)
  }

  const getDealTypeLabel = (dealType: string) => {
    switch (dealType) {
      case "FIXED":
        return "Fixed Deal"
      case "CONFIGURABLE":
        return "Customizable"
      case "COMPLEX":
        return "Premium Deal"
      default:
        return dealType
    }
  }

  const handleViewDeal = () => {
    navigate(`/deals/${deal.id}`)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <Card className={`group cursor-pointer hover:shadow-lg transition-all duration-300 overflow-hidden ${className}`}>
        <div className="relative">
          <img
            src={deal.image}
            alt={deal.name}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {deal.featured && (
            <Badge className="absolute top-2 left-2 bg-yellow-500 hover:bg-yellow-600">
              <Star className="w-3 h-3 mr-1 fill-current" />
              Featured
            </Badge>
          )}
          <Badge
            variant="secondary"
            className="absolute top-2 right-2"
          >
            {getDealTypeLabel(deal.dealType)}
          </Badge>
        </div>

        <CardContent className="p-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg line-clamp-1">{deal.name}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {deal.description}
            </p>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-primary">
                  {formatPrice(deal.price)}
                </span>
                {deal.originalPrice && deal.originalPrice > deal.price && (
                  <span className="text-sm text-muted-foreground line-through">
                    {formatPrice(deal.originalPrice)}
                  </span>
                )}
              </div>
              <Badge variant="outline">
                {deal.components.length} component{deal.components.length !== 1 ? "s" : ""}
              </Badge>
            </div>

            <Button className="w-full mt-4" size="sm" onClick={handleViewDeal}>
              View Deal
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}